// 2차 인증(MFA 증표·step-up·수단 등록) 공통 처리. 계약은 SPEC-SENDCERT-001 design.md §2·§3·§6-1.
// admin-hospital·admin-manufacturer 가 같은 내용을 씀. 저장 키는 tokenStore(지원 탭이면 그 탭 sessionStorage)가 정함.
import {
  getMfaToken as storedMfaToken,
  setMfaToken as storeMfaToken,
  setAccessToken,
  setRefreshToken,
} from "./tokenStore.js";

export const GEO_BLOCKED_MESSAGE =
  "해외에서는 이용할 수 없습니다. 예외 신청은 서티라이프 운영팀에 해 주세요.";
export const ENFORCED_MESSAGE =
  "2차 인증이 필수인 계정이라 마지막 수단은 지울 수 없습니다. 다른 수단을 먼저 등록해 주세요.";
// 503 MFA_CHECK_UNAVAILABLE 고정 문구(crm 운영 필터가 5xx message 를 지우므로 서버 문구를 쓰지 않음, F-9)
export const MFA_CHECK_UNAVAILABLE_MESSAGE =
  "보안 상태를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.";
export const RESET_PROTECTED_MESSAGE =
  "대표·관리자 계정은 서티라이프 운영팀에 초기화를 요청하세요.";
// 인증번호를 못 받을 때(번호·이메일이 바뀜) 갈 길: 로그인 2단계·본인 확인 창 아래 안내와 발송 실패 문구 끝(D4)
export const RESET_HINT =
  "휴대폰이나 이메일을 바꿨다면 회사 관리자에게 「2차 인증 초기화」를 요청하세요.";
export const NOT_RECEIVED_HELP = `인증번호가 오지 않나요? ${RESET_HINT}`;
// 직원 목록 「로그인 잠김」 배지 설명. 초기화(DELETE /accounts/:id/two-factor)는 수단만 지우고 잠김은 풀지 않음
export const LOCKED_HINT =
  "로그인 실패가 반복되어 잠긴 계정입니다. 시간이 지나면 풀립니다(2차 인증 초기화로는 안 풀림).";

// 수단 이름(design.md §2-3). FALLBACK_EMAIL_OTP 는 옛 이름이고 뜻은 "본인 이메일 수단".
export const METHOD_LABELS = {
  ALIMTALK_OTP: "휴대폰(카카오 알림톡)",
  FALLBACK_EMAIL_OTP: "이메일",
  EMAIL_OTP: "이메일(이전 등록)",
};

export function getMfaToken() {
  return storedMfaToken();
}

// 값이 없으면 지움(다른 계정의 옛 증표가 남지 않게)
export function setMfaToken(token) {
  storeMfaToken(token);
}

// 세션 중 수단 등록·삭제(A15·A16) 응답의 새 증표·토큰 저장.
// studio iframe 안(admin-hospital)에서는 토큰을 studio 가 관리하므로 증표만 바꿈.
export function storeRenewedTokens(data) {
  if (data?.mfaToken) setMfaToken(data.mfaToken);
  if (!data?.accessToken || window.parent !== window) return;
  setAccessToken(data.accessToken);
  if (data.refreshToken) setRefreshToken(data.refreshToken);
  // 앱 웹뷰에도 새 토큰을 알림(admin-manufacturer 만 있는 다리, 없으면 건너뜀)
  window.checkIsLoggedIn?.({
    isLoggedIn: true,
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
  });
}

export function errorCode(error) {
  return error?.response?.data?.code;
}

// 로그인 응답 → 인증번호 창 모드: 2단계 'login', 수단 등록 'enroll', 바로 토큰이면 undefined
export function loginStepMode(data) {
  if (data?.code === "TWO_FACTOR_ENROLL_REQUIRED" || data?.enrollRequired)
    return "enroll";
  if (data?.code === "TWO_FACTOR_REQUIRED" || data?.twoFactorRequired)
    return "login";
  return undefined;
}

// 403 MFA_REQUIRED → LOGIN, 403 SEND_AUTH_REQUIRED → SEND, 그 밖 undefined
export function stepUpPurpose(error) {
  if (error?.response?.status !== 403) return undefined;
  return { MFA_REQUIRED: "LOGIN", SEND_AUTH_REQUIRED: "SEND" }[
    errorCode(error)
  ];
}

// 모든 요청의 403 GEO_BLOCKED 안내 문구(서버 문구 우선), 아니면 null (design.md §2-3)
export function geoBlockedNotice(error) {
  if (error?.response?.status !== 403 || errorCode(error) !== "GEO_BLOCKED")
    return null;
  return authErrorMessage(error);
}

// 503 MFA_CHECK_UNAVAILABLE("지금 2FA 가 필요한가"를 확인 못 함, design.md §2-2) → 고정 문구, 아니면 null.
// 인터셉터가 한 번 알리고 실패로 끝냄 — 재전송·step-up 없음(F-9)
export function mfaCheckUnavailableNotice(error) {
  return error?.response?.status === 503 &&
    errorCode(error) === "MFA_CHECK_UNAVAILABLE"
    ? MFA_CHECK_UNAVAILABLE_MESSAGE
    : null;
}

// 2차 인증 초기화 버튼을 숨길 행: 서버가 대표·회사 관리자로 판정한 행(twoFactor.resetProtected, D-129).
// accountType 으로는 판단하지 않음(제조사·기공소 직원은 기본값 ADMIN 으로 저장돼 있음, F-14).
// 값이 없는 옛 서버면 보여 두고, 거절되면 서버 403 문구(resetErrorMessage)로 안내.
export function isResetProtected(row) {
  return row?.twoFactor?.resetProtected === true;
}

// 2차 인증 초기화 실패 문구. 대상 보호 403(대표·관리자·자기 계정)은 운영자에게 요청하라고 안내, 그 밖은 authErrorMessage
export function resetErrorMessage(error) {
  const data = error?.response?.data || {};
  const protectedTarget =
    data.code === "NOT_COMPANY_MANAGER" &&
    ["REPRESENTATIVE_TARGET", "MANAGER_TARGET", "SELF_TARGET"].includes(
      data.reason
    );
  return protectedTarget ? RESET_PROTECTED_MESSAGE : authErrorMessage(error);
}

// 프로필 mfa(§5-3) → 쓸 수 있는 수단 목록
export function profileMethods(mfa) {
  return [
    mfa?.phoneMasked && "ALIMTALK_OTP",
    mfa?.fallbackEmailMasked && "FALLBACK_EMAIL_OTP",
  ].filter(Boolean);
}

// 직원 목록의 2차 인증 칸(B5·B7 twoFactor{enabled, selfDisabled, phoneMasked, fallbackEmailMasked}) → 켜짐/꺼짐 배지 + 등록된 수단 이름,
// 본인이 끈 행(꺼짐 + selfDisabled, D-132)은 note 「본인이 끔」. 회사 강제로 켜진 행(enabled + selfDisabled)은 실제로 2단계를 거치므로 켜짐 그대로.
// 같은 줄 연락처가 전체 번호라 가린 값은 보이지 않음
export function twoFactorCell(twoFactor) {
  const methods = [
    twoFactor?.phoneMasked && "휴대폰",
    twoFactor?.fallbackEmailMasked && "이메일",
  ].filter(Boolean);
  const on = !!(twoFactor?.enabled ?? methods.length);
  return {
    on,
    methods: methods.join("·"),
    note: !on && twoFactor?.selfDisabled === true ? "본인이 끔" : "",
  };
}

// 확인창·알림에 쓰는 직원 표시: 동명이인을 가르려고 아이디를 붙임(D7)
export function accountName(record) {
  return record?.name
    ? `${record.name}(${record.username})`
    : record?.username || "";
}

// 「2차 인증 초기화」 확인 문구(D7). 회사 필수면 다음 로그인 때 다시 등록, 아니면 꺼짐
export function resetConfirmText(record, companyRequired) {
  return `${accountName(record)}님의 2차 인증을 초기화합니다. ${
    companyRequired
      ? "등록된 휴대폰·이메일이 지워지고, 다음 로그인 때 다시 등록합니다."
      : "등록된 휴대폰·이메일이 지워져 2차 인증이 꺼집니다. 필요하면 본인이 다시 등록합니다."
  }`;
}

// 직원 목록 위 회사 강제 상태 안내(회사 강제는 서티라이프 운영팀만 바꿈, U-20·D-79)
export function companyPolicyText(required) {
  return required
    ? "회사 전체 2차 인증 필수: 켜짐 · 모든 직원이 로그인할 때 인증번호를 입력합니다. 설정은 서티라이프 운영팀이 바꿉니다."
    : "회사 전체 2차 인증 필수: 꺼짐 · 휴대폰이나 이메일을 등록한 직원만 인증번호를 입력합니다. 설정은 서티라이프 운영팀이 바꿉니다.";
}

// 휴대폰 칸 표시(010-0000-0000). 입력 중에도 3-4-4 로 끊어 보임
export function formatPhone(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

// 인터셉터가 이미 알렸거나(국외 차단·2차 인증 확인 실패·새 오류 코드 standardErrors) 사용자가 본인 확인 창을 스스로 닫은 실패
// → 화면이 또 「실패」를 띄우지 않음(D5, #17·#19, W6 E2)
export function alreadyNotified(error) {
  return (
    error?.stepUpCancelled === true ||
    error?.standardNotified === true ||
    !!geoBlockedNotice(error) ||
    !!mfaCheckUnavailableNotice(error)
  );
}

function clock(iso) {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
}

// 오류 코드 → 화면 문구(design.md §2-3). 코드가 없거나 모르는 코드면 null(호출부의 기존 문구 사용).
// LOGIN_FAILED 는 화면마다 문구가 달라 여기서 다루지 않음(remainingAttempts 만 덧붙임).
export function authErrorMessage(error) {
  const data = error?.response?.data || {};
  switch (data.code) {
    case "GEO_BLOCKED":
      return `${data.message || GEO_BLOCKED_MESSAGE}${
        data.ip ? ` (접속 IP: ${data.ip})` : ""
      }`;
    case "ACCOUNT_LOCKED": {
      const until = clock(data.lockedUntil);
      return `로그인 실패가 반복되어 ${
        until ? `${until}까지 ` : ""
      }로그인이 잠겼습니다.`;
    }
    case "TWO_FACTOR_NOT_ASSIGNED":
      return "2차 인증 수단이 없습니다. 휴대폰 또는 이메일을 먼저 등록해 주세요.";
    case "TWO_FACTOR_ENFORCED":
      return ENFORCED_MESSAGE;
    case "SESSION_NOT_ALLOWED":
      return (
        data.message ||
        "이 로그인 방식으로는 2차 인증 수단을 등록·변경할 수 없습니다. 본인 아이디·비밀번호로 로그인해 주세요."
      );
    case "PHONE_DUPLICATED":
      return "같은 회사의 다른 계정이 이미 쓰는 번호입니다.";
    case "EMAIL_DUPLICATED":
      return "같은 회사의 다른 계정이 이미 쓰는 이메일입니다.";
    // 지원 서브 계정은 비밀번호 로그인 불가(계약 SUPPORT 3) — 로그인 화면 문구(W6 E2·C8)
    case "SUPPORT_ACCOUNT_LOGIN_DENIED":
      return "지원 계정은 이 화면으로 로그인할 수 없습니다.";
    case "IMPERSONATION_REISSUE_REQUIRED":
      return "리셀러 화면에서 다시 들어와 주세요.";
    case "OTP_INVALID":
      return `인증번호가 올바르지 않습니다.${
        data.remainingAttempts != null
          ? ` (남은 횟수 ${data.remainingAttempts}회)`
          : ""
      }`;
    case "OTP_EXPIRED":
      return "인증번호가 만료되었습니다. 인증번호를 다시 받아 주세요.";
    // 남은 초는 인증번호 창 버튼이 줄여 가며 보임(멈춘 숫자를 문구에 넣지 않음, D3)
    case "OTP_RATE_LIMITED":
      return "잠시 후 다시 요청해 주세요.";
    case "OTP_SEND_FAILED": {
      const other = (data.fallbackMethods || []).find((m) => METHOD_LABELS[m]);
      return other
        ? `인증번호를 보내지 못했습니다. ${METHOD_LABELS[other]} 수단으로 받아 주세요.`
        : "인증번호를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.";
    }
    default:
      return null;
  }
}

// LOGIN_FAILED 의 남은 횟수(연속 3번째부터 서버가 줌)를 기존 문구에 덧붙임
export function withRemainingAttempts(message, error) {
  const remaining = error?.response?.data?.remainingAttempts;
  return remaining != null ? `${message} (남은 시도 ${remaining}회)` : message;
}

// step-up 창은 App.vue 가 등록. 같은 목적의 동시 요청은 창 하나를 같이 기다림.
let stepUpHandler = null;
const pending = {};

export function setStepUpHandler(handler) {
  stepUpHandler = handler;
}

export function requestStepUp(purpose) {
  if (!stepUpHandler) return Promise.resolve(false);
  if (!pending[purpose]) {
    pending[purpose] = Promise.resolve(stepUpHandler(purpose))
      .then(Boolean)
      .finally(() => delete pending[purpose]);
  }
  return pending[purpose];
}
